/**
 * storeConfig — Config Engine de Tiendas.
 *
 * Fuente única de defaults y tipos de configuración por tienda.
 * Nadie debe asumir valores de stores.config sin pasar por getStoreConfig().
 * Nadie debe leer storeConfig directamente para decisiones operativas — usar storeRules.ts.
 *
 * Uso:
 *   const cfg = getStoreConfig(cajero?.storeConfig)
 *   const rule = canUseCredit(cfg)  // <-- Rules Engine decide
 */

// ── Tipos ───────────────────────────────────────────────────────────────────

export interface StoreConfigFiscal {
  tax_enabled: boolean
  show_tax_breakdown: boolean
  default_tax_rate: number
}

export interface StoreConfigInventory {
  reserve_cart_stock: boolean
  held_cart_ttl_hours: number
  allow_negative_stock: boolean
}

export interface StoreConfigCash {
  require_cash_count: boolean
  require_close_signature: boolean
  require_difference_reason: boolean
  allow_cash_in: boolean
  allow_cash_out: boolean
}

export interface StoreConfigWarranties {
  allow_warranty_from_pos: boolean
  print_warranty_receipt: boolean
}

export interface StoreConfigSales {
  allow_credit: boolean
  allow_layaway: boolean
  allow_quote: boolean
  allow_order: boolean
  allow_discount: boolean
  require_pin_for_discount: boolean
  discount_pin_threshold_percent: number
  // Venta por la Edge Function segura `confirmar_venta` (RPC transaccional). 00405: es la regla salvo un false
  // EXPLÍCITO en la tienda (salida de emergencia por SQL); ausente o null ⇒ servidor, igual que la base.
  server_authoritative: boolean
  // P0-1 Fase 8E: si true (y server_authoritative), permite CAPTURAR ventas
  // ordinarias en efectivo SIN conexión como intención pendiente (nunca venta
  // confirmada). Default OFF. Gate por tienda. El servidor valida al sincronizar.
  offline_intents_enabled: boolean
}

export interface StoreConfigPostSale {
  allow_store_credit: boolean
  allow_cancel_sale: boolean
  allow_return_sale: boolean
}

export interface StoreConfigRepairs {
  enabled: boolean
}

export interface StoreConfig {
  fiscal: StoreConfigFiscal
  inventory: StoreConfigInventory
  cash: StoreConfigCash
  warranties: StoreConfigWarranties
  sales: StoreConfigSales
  postSale: StoreConfigPostSale
  repairs: StoreConfigRepairs
}

// ── Defaults seguros ────────────────────────────────────────────────────────

const DEFAULTS: StoreConfig = {
  fiscal: {
    tax_enabled: true,
    show_tax_breakdown: true,
    default_tax_rate: 16,
  },
  inventory: {
    reserve_cart_stock: true,
    held_cart_ttl_hours: 24,
    allow_negative_stock: false,
  },
  cash: {
    require_cash_count: true,
    require_close_signature: true,
    require_difference_reason: true,
    allow_cash_in: true,
    allow_cash_out: true,
  },
  warranties: {
    allow_warranty_from_pos: true,
    print_warranty_receipt: true,
  },
  sales: {
    allow_credit: true,
    allow_layaway: true,
    allow_quote: true,
    allow_order: true,
    allow_discount: true,
    require_pin_for_discount: false,
    discount_pin_threshold_percent: 20,
    server_authoritative: true,
    offline_intents_enabled: false,
  },
  postSale: {
    allow_store_credit: true,
    allow_cancel_sale: true,
    allow_return_sale: true,
  },
  // Default opt-out: el Taller queda activo salvo que la tienda lo apague.
  repairs: {
    enabled: true,
  },
}

// ── Helper principal ────────────────────────────────────────────────────────

/**
 * Devuelve la configuración completa de una tienda.
 * Hace merge superficial por sección — claves desconocidas se ignoran.
 * Siempre devuelve una StoreConfig completa aunque rawConfig esté vacío.
 */
export function getStoreConfig(rawConfig: Record<string, unknown> = {}): StoreConfig {
  return {
    fiscal: {
      ...DEFAULTS.fiscal,
      ...((rawConfig.fiscal as Partial<StoreConfigFiscal>) ?? {}),
    },
    inventory: {
      ...DEFAULTS.inventory,
      ...((rawConfig.inventory as Partial<StoreConfigInventory>) ?? {}),
    },
    cash: {
      ...DEFAULTS.cash,
      ...((rawConfig.cash as Partial<StoreConfigCash>) ?? {}),
    },
    warranties: {
      ...DEFAULTS.warranties,
      ...((rawConfig.warranties as Partial<StoreConfigWarranties>) ?? {}),
    },
    sales: {
      ...DEFAULTS.sales,
      ...((rawConfig.sales as Partial<StoreConfigSales>) ?? {}),
      // Un null o cualquier valor que no sea false explícito NO abre la ruta vieja (00405, venta_exige_servidor).
      server_authoritative:
        (rawConfig.sales as Partial<StoreConfigSales> | undefined)?.server_authoritative !== false,
    },
    postSale: {
      ...DEFAULTS.postSale,
      ...((rawConfig.postSale as Partial<StoreConfigPostSale>) ?? {}),
    },
    repairs: {
      ...DEFAULTS.repairs,
      ...((rawConfig.repairs as Partial<StoreConfigRepairs>) ?? {}),
    },
  }
}

/**
 * Merge profundo de config — no borra claves desconocidas en stores.config.
 * Usar antes de guardar para preservar claves de versiones futuras.
 */
export function mergeStoreConfig(
  current: Record<string, unknown>,
  patch: Partial<StoreConfig>
): Record<string, unknown> {
  return {
    ...current,
    ...(patch.fiscal !== undefined && {
      fiscal: { ...((current.fiscal as object) ?? {}), ...patch.fiscal },
    }),
    ...(patch.inventory !== undefined && {
      inventory: { ...((current.inventory as object) ?? {}), ...patch.inventory },
    }),
    ...(patch.cash !== undefined && {
      cash: { ...((current.cash as object) ?? {}), ...patch.cash },
    }),
    ...(patch.warranties !== undefined && {
      warranties: { ...((current.warranties as object) ?? {}), ...patch.warranties },
    }),
    ...(patch.sales !== undefined && {
      sales: { ...((current.sales as object) ?? {}), ...patch.sales },
    }),
    ...(patch.postSale !== undefined && {
      postSale: { ...((current.postSale as object) ?? {}), ...patch.postSale },
    }),
    ...(patch.repairs !== undefined && {
      repairs: { ...((current.repairs as object) ?? {}), ...patch.repairs },
    }),
  }
}
